import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Home, FileText, Settings, LogOut, User, Flag, Play, Route } from 'lucide-react';

const bigItem = 'h-14 px-4 text-base cursor-pointer [&>svg]:size-6';

function MobileTab({ to, icon: Icon, label, active, color = 'text-gray-600', testId, onClick }) {
  const cls = `flex flex-col items-center justify-center gap-1 flex-1 min-w-0 h-full rounded-xl active:scale-95 transition-transform select-none ${active ? 'text-orange-500' : color}`;
  const content = (
    <>
      <Icon className="w-8 h-8" strokeWidth={active ? 2.5 : 2} />
      <span className={`text-xs leading-none truncate ${active ? 'font-bold' : 'font-medium'}`}>{label}</span>
    </>
  );
  if (onClick) {
    return <button type="button" onClick={onClick} className={cls} data-testid={testId}>{content}</button>;
  }
  return <Link to={to} className={cls} data-testid={testId}>{content}</Link>;
}

export default function Navigation({ user, logout, isRallyMaster }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const showRallyMaster = isRallyMaster || user.is_admin;

  return (
    <>
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-16">
            <Link to="/" className="flex items-center gap-2 group" data-testid="nav-home-link">
              <img src="/logo-icon-new.png" alt="Strategos Overland" className="h-10 w-auto object-contain group-hover:scale-105 transition-transform" />
              <span className="text-xl bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent hidden lg:block tracking-wider font-semibold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                STRATEGOS OVERLAND
              </span>
            </Link>

            <div className="flex items-center gap-2 md:gap-4">
              <div className="hidden md:flex items-center gap-2 md:gap-4">
                <Link to="/" data-testid="nav-events-link">
                  <Button variant="ghost" className="gap-2"><Home className="w-4 h-4" />Eventos</Button>
                </Link>
                <Link to="/my-registrations" data-testid="nav-registrations-link">
                  <Button variant="ghost" className="gap-2"><FileText className="w-4 h-4" />Mis Inscripciones</Button>
                </Link>
                <Link to="/profile#track-recorder" data-testid="nav-record-track-link">
                  <Button variant="ghost" className="gap-2 text-green-600 hover:text-green-700 hover:bg-green-50"><Play className="w-4 h-4" />Grabar Track</Button>
                </Link>
                <Link to="/profile#my-tracks" data-testid="nav-my-tracks-link">
                  <Button variant="ghost" className="gap-2 text-orange-500 hover:text-orange-600 hover:bg-orange-50"><Route className="w-4 h-4" />Mis Tracks</Button>
                </Link>
                {showRallyMaster && (
                  <Link to="/rally-master" data-testid="nav-rally-master-link">
                    <Button variant="ghost" className="gap-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50"><Flag className="w-4 h-4" />Rally Master</Button>
                  </Link>
                )}
                {user.is_admin && (
                  <Link to="/admin" data-testid="nav-admin-link">
                    <Button variant="ghost" className="gap-2"><Settings className="w-4 h-4" />Admin</Button>
                  </Link>
                )}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2 h-14 w-14 p-0 rounded-xl border-2 border-gray-300 md:h-9 md:w-auto md:px-3 md:rounded-md md:border" data-testid="user-menu-trigger">
                    <User className="w-8 h-8 md:w-4 md:h-4" />
                    <span className="hidden md:inline">{user.full_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 md:w-48" data-testid="user-menu-content">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user.full_name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} className={`${bigItem} md:h-9 md:text-sm md:[&>svg]:size-4`} data-testid="profile-btn">
                    <User className="mr-2" />Mi Perfil
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className={`${bigItem} md:h-9 md:text-sm md:[&>svg]:size-4 text-red-600`} data-testid="logout-btn">
                    <LogOut className="mr-2" />Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile bottom tab bar: big glove-friendly targets */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="mobile-tabbar"
      >
        <div className="flex items-stretch h-[76px] px-1">
          <MobileTab to="/" icon={Home} label="Eventos" active={pathname === '/'} testId="tab-events" />
          <MobileTab to="/my-registrations" icon={FileText} label="Inscripciones" active={pathname.startsWith('/my-registrations')} testId="tab-registrations" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="flex flex-col items-center justify-center gap-1 flex-1 min-w-0 rounded-xl text-green-600 active:scale-95 transition-transform select-none" data-testid="tab-tracks">
                <Route className="w-8 h-8" />
                <span className="text-xs leading-none font-medium">Tracks</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="center" className="w-60 mb-2">
              <DropdownMenuItem onClick={() => navigate('/profile#track-recorder')} className={bigItem} data-testid="tab-record-track">
                <Play className="mr-3 text-green-600" />Grabar Track
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profile#my-tracks')} className={bigItem} data-testid="tab-my-tracks">
                <Route className="mr-3 text-orange-500" />Mis Tracks
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {showRallyMaster && (
            <MobileTab to="/rally-master" icon={Flag} label="Rally" color="text-purple-600" active={pathname.startsWith('/rally-master')} testId="tab-rally-master" />
          )}
          {user.is_admin && (
            <MobileTab to="/admin" icon={Settings} label="Admin" active={pathname.startsWith('/admin')} testId="tab-admin" />
          )}
        </div>
      </nav>
    </>
  );
}
